'use client'
import { cn } from '@/lib/utils'
import { ChevronDown, CreditCard, HelpCircle, Laptop, type LucideIcon, MessageCircle, ShieldCheck, Users, Zap } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { type FC, type KeyboardEvent, useRef, useState } from 'react'
import { FAQ_CATEGORIES, type FaqCategory as Category } from './faq-categories'

interface FaqItem {
  question: string
  answer: string
}

const CATEGORY_ICONS: Record<Category | 'pricingAndSubscription', LucideIcon> = {
  seoQuestions: HelpCircle,
  gettingStarted: Zap,
  suitability: Users,
  addOnsAndCustomization: Laptop,
  support: MessageCircle,
  securityAndGdpr: ShieldCheck,
  pricingAndSubscription: CreditCard,
}

const FaqSection: FC = () => {
  const t = useTranslations('FaqContent')
  const [active, setActive] = useState<Category>(FAQ_CATEGORIES[0])
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = FAQ_CATEGORIES.length - 1
    const next =
      event.key === 'ArrowDown' || event.key === 'ArrowRight'
        ? index === last
          ? 0
          : index + 1
        : event.key === 'ArrowUp' || event.key === 'ArrowLeft'
          ? index === 0
            ? last
            : index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null
    if (next === null) return
    event.preventDefault()
    const category = FAQ_CATEGORIES[next]!
    setActive(category)
    tabRefs.current[category]?.focus()
  }

  return (
    <section id="faq" aria-labelledby="faq-title" className="w-full pb-8 pt-16 md:pb-10 md:pt-24">
      <div className="container px-4 md:px-6">
        <div className="mb-10 max-w-2xl space-y-3 md:mb-14">
          <h2 id="faq-title" className="text-balance text-3xl font-bold tracking-tight text-neutral-900 md:text-4xl">
            {t('commonQuestions')}
          </h2>
          <p className="text-lg text-neutral-600">{t('subtitle')}</p>
        </div>

        <div className="grid items-start gap-8 lg:grid-cols-[17rem_minmax(0,1fr)] lg:gap-12">
          <div
            role="tablist"
            aria-label={t('commonQuestions')}
            aria-orientation="vertical"
            className="flex flex-wrap gap-2 lg:sticky lg:top-24 lg:flex-col"
          >
            {FAQ_CATEGORIES.map((category, index) => {
              const Icon = CATEGORY_ICONS[category]
              const selected = active === category
              return (
                <button
                  key={category}
                  ref={(element) => {
                    tabRefs.current[category] = element
                  }}
                  type="button"
                  role="tab"
                  id={`faq-tab-${category}`}
                  aria-selected={selected}
                  aria-controls={`faq-panel-${category}`}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActive(category)}
                  onKeyDown={(event) => onTabKeyDown(event, index)}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border px-3 py-2 lg:gap-3 lg:px-4 lg:py-2.5 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                    selected
                      ? 'border-primary/20 bg-primary/10 text-neutral-900'
                      : 'border-transparent text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
                  )}
                >
                  <Icon aria-hidden className={cn('size-4 shrink-0', selected ? 'text-primary' : 'text-neutral-400')} />
                  {t(`titles.${category}`)}
                </button>
              )
            })}
          </div>

          {FAQ_CATEGORIES.map((category) => (
            <div
              key={category}
              role="tabpanel"
              id={`faq-panel-${category}`}
              aria-labelledby={`faq-tab-${category}`}
              hidden={active !== category}
              tabIndex={0}
              className="focus-visible:outline-none"
            >
              <h3 className="mb-4 text-xl font-semibold text-neutral-900">{t(`titles.${category}`)}</h3>
              <div className="divide-y divide-neutral-200 overflow-hidden rounded-xl border border-neutral-200">
                {(t.raw(category) as FaqItem[]).map((faq) => (
                  <details key={faq.question} className="group bg-white open:bg-neutral-50">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-left font-medium text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary [&::-webkit-details-marker]:hidden">
                      <h4 className="text-base">{faq.question}</h4>
                      <ChevronDown aria-hidden size={18} className="shrink-0 text-neutral-500 transition-transform group-open:rotate-180" />
                    </summary>
                    <p className="px-5 pb-5 leading-relaxed text-neutral-700">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default FaqSection
